import { useCallback } from 'react';
import { getBriefingDates } from '../services/dailyBriefing';
import { useLocalFirstQuery } from './useLocalFirstQuery';
import { useOnline } from './useOnline';
import { BRIEFING_TTL_MS } from '../db/freshness';
import { ARCHIVE_CACHE_LIMIT } from '../db/retention';

// Reason: §3.6 pins the cache key; the limit matches both the backend default
// and the archive retention ceiling, so a fully-browsed index fits in cache.
export const DATES_INDEX_KEY = 'daily-briefing-dates';
export const DATES_INDEX_LIMIT = ARCHIVE_CACHE_LIMIT;

/**
 * Local-first read of the archive dates index.
 *
 * Cached in meta['daily-briefing-dates'] exactly like meta['topic-briefing']
 * (Briefing.jsx) — cache-first so the picker and the date arrows survive
 * offline, TTL 0 + ETag so an unchanged index costs a 304. A failed
 * revalidation with a cached row is silent by hook contract, which is what keeps
 * a stale catalogue on screen instead of blanking it.
 *
 * @returns {{index: object|null, loading: boolean, error: string|null}}
 */
export const useBriefingDatesIndex = () => {
  const online = useOnline();
  const fetchDates = useCallback(
    ({ etag }) => getBriefingDates({ etag, limit: DATES_INDEX_LIMIT }),
    [],
  );

  const { data, loading, error } = useLocalFirstQuery({
    table: 'meta',
    key: DATES_INDEX_KEY,
    ttl: BRIEFING_TTL_MS,
    fetch: fetchDates,
  });

  return {
    index: data ?? null,
    loading,
    // Reason: offline is an expected state — the arrows simply go quiet and the
    // cached briefing still renders. A 500 or a 401 is not, and must say so
    // rather than being indistinguishable from "no history yet".
    error: error && online ? (error.message || 'Failed to fetch briefing dates') : null,
  };
};

export default useBriefingDatesIndex;
